import { Base64 } from 'js-base64'
import type { ParsedQuery } from 'query-string'
import { parse, stringify } from 'query-string'
import _ from 'lodash'
import moment from 'moment'
import { parse as searchQueryJsonParser } from '../../generated-search-query-json-parser.js'
import { FileType } from '../app/constants/MediaLibrary'
import type { CmFieldResponseObject } from '../app/interfaces/CustomMetadataResponse.js'
import { CheckValidObjectId } from '../app/components/MediaLibrary/utils'
import { dimensionOptions, fileTypeMapping } from './mediaLibrary'

// Field schema and validation types
type FieldDataType =
  | 'String'
  | 'Number'
  | 'Date'
  | 'Boolean'
  | 'Size'
  | 'GeoLocation'
  | 'ObjectId'
  | 'Mixed'
  | 'MixedArray'

interface FieldSchema {
  dataType: FieldDataType
  mapTo?: string
}

interface FieldSchemaMap {
  [key: string]: FieldSchema
}

// Map custom metadata field types to search query parser types
const typesMap: Record<string, FieldDataType> = {
  ObjectId: 'ObjectId',
  Text: 'String',
  Textarea: 'String',
  Number: 'Number',
  Date: 'Date',
  Boolean: 'Boolean',
  SingleSelect: 'Mixed',
  MultiSelect: 'MixedArray',
}

// Supported operators per field type
const supportedCustomMetadataFieldTypeOperatorMap: Record<
  FieldDataType,
  string[]
> = {
  String: ['IS', 'IS_NOT', 'STARTS_WITH', 'HAS', 'IS_ONE_OF', 'IS_NONE_OF', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  Number: [
    'LESS_THAN',
    'GREATER_THAN',
    'IS',
    'IS_NOT',
    'IS_ONE_OF',
    'IS_NONE_OF',
    'LESS_THAN_EQUAL',
    'GREATER_THAN_EQUAL',
    'IS_EMPTY',
    'IS_NOT_EMPTY',
  ],
  Date: ['LESS_THAN', 'GREATER_THAN', 'IS', 'LESS_THAN_EQUAL', 'GREATER_THAN_EQUAL', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  Boolean: ['IS', 'IS_ONE_OF', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  MixedArray: ['IS_ONE_OF', 'IS_NONE_OF', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  Mixed: ['IS', 'IS_NOT', 'IS_ONE_OF', 'IS_NONE_OF', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  Size: ['LESS_THAN_EQUAL', 'GREATER_THAN_EQUAL'],
  GeoLocation: ['IS', 'IS_NOT_EMPTY', 'IS_EMPTY'],
  ObjectId: ['IS', 'IS_NOT', 'IS_ONE_OF', 'IS_NONE_OF', 'IS_EMPTY', 'IS_NOT_EMPTY'],
}

// Default field schema
const getDefaultSchema = (
  customMetadataFields: CmFieldResponseObject[],
): FieldSchemaMap => {
  const fieldSchema: FieldSchemaMap = {
    name: { dataType: 'String' },
    type: { dataType: 'String' },
    tags: { dataType: 'String' },
    createdAt: { dataType: 'Date' },
    updatedAt: { dataType: 'Date' },
    height: { dataType: 'Number' },
    width: { dataType: 'Number' },
    size: { dataType: 'Size' },
    format: { dataType: 'String' },
    private: { dataType: 'Boolean' },
    published: { dataType: 'Boolean' },
    transparency: { dataType: 'Boolean' },
    'embeddedMetadata.DateTimeOriginal': { dataType: 'Date' },
    'embeddedMetadata.Keywords': { dataType: 'String' },
    'embeddedMetadata.LocationTaken': { dataType: 'GeoLocation' },
    path: { dataType: 'String' },
    description: { dataType: 'String' },
    id: { dataType: 'ObjectId' },
    createdBy: { dataType: 'String' },
  }

  // Add custom metadata fields
  if (customMetadataFields) {
    customMetadataFields.forEach(customMetadataField => {
      // Skip deleted fields (if deleted property exists)
      if ((customMetadataField as any).deleted) {
        return
      }
      const type = customMetadataField.schema.type
      if (!type) {
        return
      }
      fieldSchema[`customMetadata.${customMetadataField.name}`] = {
        dataType: typesMap[type] || 'String',
        mapTo: customMetadataField.name,
      }
    })
  }

  return fieldSchema
}

const distanceRegex = /((\d+)(\.\d+)?)(m|cm|km)/
const periodRegex = /^(\d+)([hdwmy])$/i

// Extract coordinates from coordinate string
const extractCoordinates = (coordinatesString: string): number[][] => {
  const coordinatesStringArray = coordinatesString.split('|')
  const coordinatesArray = coordinatesStringArray.map(coordString => {
    const [latitudeString, longitudeString] = coordString.split(',')
    const latitude = Number.parseFloat(latitudeString)
    const longitude = Number.parseFloat(longitudeString)
    return [longitude, latitude]
  })
  return coordinatesArray
}

// Get date from period (e.g., "1d", "2w", "5m", "1y")
const getDateFromPeriod = (
  startDate: moment.Moment,
  number: string,
  quantifier: string,
  _operator: string,
): moment.Moment => {
  let finalDate = startDate.clone()
  switch (quantifier) {
    case 'h':
      finalDate = finalDate.subtract(parseInt(number, 10), 'hours')
      break
    case 'd':
      finalDate = finalDate.subtract(parseInt(number, 10), 'days')
      break
    case 'w':
      finalDate = finalDate.subtract(parseInt(number, 10), 'weeks')
      break
    case 'm':
      finalDate = finalDate.subtract(parseInt(number, 10), 'months')
      break
    case 'y':
      finalDate = finalDate.subtract(parseInt(number, 10), 'years')
      break
    default:
      break
  }
  return finalDate
}

// Helper functions for field validation
const getFieldDataType = (
  field: string,
  schema: FieldSchemaMap,
): FieldDataType | null => schema[field].dataType || null

const isCustomMetadataField = (field: string): boolean =>
  field.includes('customMetadata.')

const validateOperatorForFieldType = (
  fieldType: FieldDataType,
  operator: string,
): boolean => {
  const allowedOperators =
    supportedCustomMetadataFieldTypeOperatorMap[fieldType]
  return allowedOperators ? allowedOperators.includes(operator) : false
}

const validateSingleValueForFieldType = (
  fieldType: FieldDataType,
  value: any,
): boolean => {
  switch (fieldType) {
    case 'String':
      return typeof value === 'string'
    case 'Number':
      return typeof value === 'number' && !isNaN(value)
    case 'Date':
      return value instanceof Date || !isNaN(Date.parse(value))
    case 'Boolean':
      return typeof value === 'boolean'
    case 'Mixed':
    case 'MixedArray':
      return (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      )
    case 'Size':
      return typeof value === 'string' && /^\d+[A-Z]*$/i.test(value)
    case 'GeoLocation':
      return typeof value === 'string'
    case 'ObjectId':
      return typeof value === 'string'
    default:
      return true
  }
}

const validateValueForFieldType = (
  fieldType: FieldDataType,
  value: any,
  operator?: string,
): boolean => {
  if (
    (value === null || typeof value === 'undefined') &&
    ['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator ?? '')
  ) {
    return true
  }

  // Handle array values for IS_ONE_OF and IS_NONE_OF operators
  if (Array.isArray(value)) {
    return value.every(item => validateSingleValueForFieldType(fieldType, item))
  }

  // Handle single values
  return validateSingleValueForFieldType(fieldType, value)
}

export const containsOr = (obj: any): boolean => {
  if (obj.boolean === 'or') {
    return true
  }
  if (obj.expressions) {
    return obj.expressions.some((expression: any) => containsOr(expression))
  }
  return false
}

export const containsDuplicateField = (obj: any): boolean => {
  const fieldNames: { [key: string]: boolean } = {}

  const traverse = (o: any) => {
    if (o.fieldName) {
      if (fieldNames[o.fieldName]) {
        return true
      }
      fieldNames[o.fieldName] = true
    } else if (o.expressions) {
      for (const expression of o.expressions) {
        if (traverse(expression)) {
          return true
        }
      }
    }
    return false
  }
  return traverse(obj)
}

// Helper function to check if a search query contains a type field
const hasTypeField = (expr: any): boolean => {
  if (!expr || typeof expr !== 'object') {
    return false
  }

  // Check if this expression has a type field
  if (expr.fieldName === 'type') {
    return true
  }

  // Recursively check nested expressions
  if (expr.expressions && Array.isArray(expr.expressions)) {
    return expr.expressions.some((subExpr: any) => hasTypeField(subExpr))
  }

  return false
}

export const translateSearchQueryParamsToNewApi = ({
  queryParams,
  customMetadataFields,
}: {
  queryParams: ParsedQuery
  customMetadataFields?: CmFieldResponseObject[]
}): ParsedQuery => {
  // Create a copy to avoid mutating the original object
  const translatedParams = { ...queryParams }

  translatedParams.view = null
  translatedParams.searchQueryJson = null

  if (translatedParams.semantic) {
    translatedParams.q = translatedParams.semantic
    translatedParams.semantic = null
    translatedParams.searchMode = 'ai-text'
  } else if (translatedParams.semanticUrl) {
    translatedParams.q = translatedParams.semanticUrl
    translatedParams.semanticUrl = null
    translatedParams.searchMode = 'ai-image'
  } else if (translatedParams.nameValue) {
    translatedParams.q = Array.isArray(translatedParams.nameValue)
      ? translatedParams.nameValue[0]
      : translatedParams.nameValue
    translatedParams.nameValue = null
    translatedParams.searchMode = 'keyword'
    translatedParams.searchInputMode = 'builder'
  }

  // Normalize searchQuery (but don't parse yet)
  translatedParams.searchQuery = Array.isArray(translatedParams.searchQuery)
    ? translatedParams.searchQuery[0]
    : translatedParams.searchQuery

  if (translatedParams.isFolderRestrictedSearch === 'true') {
    translatedParams.isFolderRestrictedSearch = null
  }

  if (translatedParams.type) {
    const decodedType = Base64.decode(
      Array.isArray(translatedParams.type)
        ? translatedParams.type[0]
        : translatedParams.type,
    )

    // making the assumption here that `type` is used only to enable to include file versions option
    if (decodedType === 'type IN ["file", "file-version"]') {
      translatedParams.isVersionIncludedInSearch = 'true'
    }
    translatedParams.type = null
  }

  // Attempt to parse searchQuery LAST (after all other translations)
  // This ensures it doesn't get overridden by other logic
  if (translatedParams.searchQuery && customMetadataFields) {
    try {
      // Use the new combined function that handles everything
      parseAndConvertSearchQuery(
        translatedParams.searchQuery as string,
        customMetadataFields,
      )
      // Optionally set searchQueryJson if needed (currently set to null above)
      // translatedParams.searchQueryJson = searchQueryJson
    } catch (err) {
      // Any error (complex query, parsing failure, etc.) → query mode
      // Match the exact behavior from MLProvider.tsx lines 1062-1068
      translatedParams.searchInputMode = 'query'
      translatedParams.q = ''
      translatedParams.searchByTags = 'false'
      translatedParams.searchMode = null
      translatedParams.minScore = null
      translatedParams.fileType = null
    }
  }

  return translatedParams
}

/**
 * Normalizes a search URL for comparison by translating old params and dropping unnecessary ones
 * @param url - The search URL to normalize
 * @param customMetadataFields - Optional custom metadata fields for parsing searchQuery
 * @returns Normalized query string for comparison, or empty string if parsing fails
 */
export const normalizeSearchUrlForComparison = (
  url: string,
  customMetadataFields?: CmFieldResponseObject[],
): string => {
  try {
    const urlObj = new URL(url)
    const queryParams = parse(urlObj.search)

    // Translate old params to new API format
    const translatedParams = translateSearchQueryParamsToNewApi({
      queryParams,
      customMetadataFields,
    })

    // Keep only relevant search params, drop unnecessary ones
    const relevantParams: Record<string, string> = {}
    const relevantKeys = [
      'q',
      'searchQuery',
      'searchMode',
      'searchInputMode',
      'searchByTags',
      'isVersionIncludedInSearch',
      'fileType',
    ]

    relevantKeys.forEach(key => {
      const value = translatedParams[key]
      if (value !== null && value !== undefined && value !== '') {
        // Drop searchByTags when it's true (default value)
        if (key === 'searchByTags' && value === 'true') {
          return // Skip adding searchByTags when it's true
        }

        if (key === 'searchInputMode' && value === 'builder') {
          return // Skip adding searchInputMode when it's 'builder'
        }

        if (key === 'searchMode' && value === 'keyword') {
          return // Skip adding searchMode when it's 'keyword'
        }

        if (key === 'fileType' && value === '') {
          return // Skip adding fileType when it's ''
        }

        if (key === 'isVersionIncludedInSearch' && value === 'false') {
          return // Skip adding isVersionIncludedInSearch when it's 'false'
        }

        // Special handling for searchQuery: decode and remove all spaces
        if (key === 'searchQuery') {
          try {
            // Decode URL-encoded searchQuery
            const decoded = decodeURIComponent(String(value))
            // Remove all spaces
            const normalized = decoded.replace(/\s+/gu, '')

            relevantParams[key] = normalized
          } catch (e) {
            // If decoding fails, just use the original value with spaces removed
            relevantParams[key] = String(value).replace(/\s+/gu, '')
          }
        } else {
          relevantParams[key] = Array.isArray(value) ? value[0] : String(value)
        }
      }
    })

    // Sort keys alphabetically for consistent comparison
    const sortedKeys = Object.keys(relevantParams).sort((a, b) =>
      a.localeCompare(b),
    )
    const normalizedQuery: Record<string, string> = {}
    sortedKeys.forEach(key => {
      normalizedQuery[key] = relevantParams[key]
    })

    return stringify(normalizedQuery, { skipNull: true })
  } catch (e) {
    // If URL parsing fails, return empty string
    return ''
  }
}

/**
 * Normalizes a search query string by:
 * - Trimming leading/trailing whitespace
 * - Collapsing multiple consecutive spaces into single space
 * - Removing spaces immediately after opening parentheses
 * - Removing spaces immediately before closing parentheses
 * 
 * @param s - The search query string to normalize
 * @returns Normalized search query string
 * 
 * @example
 * canonicalize("  test    query  ") // "test query"
 * canonicalize("( test )") // "(test)"
 */
export const canonicalize = (s: string): string =>
  (s || '').trim().replace(/\s+/g, ' ').replace(/\(\s+/g, '(').replace(/\s+\)/g, ')')

// Enhanced validation logic for search query parsing with custom metadata support
const validateSearchFilters = (
  { field, operator: rawOperator, value }: any,
  envelope: any,
) => {
  let operator = rawOperator

  if (operator === 'EXISTS') {
    operator = 'IS_NOT_EMPTY'
  } else if (operator === 'NOT_EXISTS') {
    operator = 'IS_EMPTY'
  }

  const response = {
    result: {
      fieldName: field,
      operator,
      value,
    },
    error: null as string | null,
  }

  // Basic validation
  if (!field || !operator) {
    response.error = 'Invalid field or operator'
    return response
  }

  // Get field schema
  const customMetadataFields = envelope?.customMetadataFields || []
  const fieldSchema = getDefaultSchema(customMetadataFields)
  const fieldType = getFieldDataType(field, fieldSchema)

  // Check if field exists in schema
  if (!fieldType) {
    response.error = `Field '${field}' not found in schema`
    return response
  }

  // Validate operator for field type
  if (!validateOperatorForFieldType(fieldType, operator)) {
    response.error = `Invalid operator '${operator}' for field '${field}' of type '${fieldType}'`
    return response
  }

  // Validate value for field type
  if (!validateValueForFieldType(fieldType, value, operator)) {
    response.error = `Invalid value for field '${field}' of type '${fieldType}'`
    return response
  }

  if (operator === 'IS_EMPTY' || operator === 'IS_NOT_EMPTY') {
    if (
      field.includes('embeddedMetadata.') ||
      field.includes('customMetadata.')
    ) {
      response.result.fieldName = `"${field}"`
    }
    return response
  }

  // Special validation for id field: must be a valid MongoDB ObjectId
  if (field === 'id' && fieldType === 'ObjectId') {
    // Handle array values for IS_ONE_OF and IS_NONE_OF operators
    if (Array.isArray(value)) {
      const invalidIds = value.filter(
        (id: any) => typeof id !== 'string' || !CheckValidObjectId(id),
      )
      if (invalidIds.length > 0) {
        response.error = `Invalid MongoDB ObjectId value(s) for field '${field}'`
        return response
      }
    } else {
      // Handle single value
      if (typeof value !== 'string' || !CheckValidObjectId(value)) {
        response.error = `Invalid MongoDB ObjectId value for field '${field}'`
        return response
      }
    }
  }

  // Handle special field transformations
  if (fieldType === 'Date') {
    let momentDate
    const periodMatch = value.match(periodRegex)
    if (periodMatch) {
      const number = periodMatch[1]
      const quantifier = periodMatch[2].toLowerCase()
      const cutOffDate = getDateFromPeriod(
        moment(),
        number,
        quantifier,
        operator,
      )
      momentDate = cutOffDate
    } else {
      momentDate = moment.utc(value.replace(' ', 'T'))
      if (!momentDate.isValid()) {
        return null
      }
    }

    response.result.value = momentDate.toDate()
    if (operator === 'LESS_THAN') {
      response.result.operator = 'EARLIER_THAN'
    }
    if (operator === 'GREATER_THAN') {
      response.result.operator = 'LATER_THAN'
    }
    if (operator === 'IS') {
      response.result.operator = 'EXACTLY'
    }
    // handle date value
    if (
      ['EARLIER_THAN', 'LATER_THAN', 'EXACTLY'].includes(
        response.result.operator,
      )
    ) {
      response.result.value = {
        exact: response.result.value,
      }
    }
  } else if (fieldType === 'GeoLocation') {
    // handling geo location type
    if (distanceRegex.test(value)) {
      // values like "50,100 5km"
      response.result.operator = 'GEO_POINT'
    } else {
      // values like "50,100|60,150|-50,90|40,50"
      const coordinatesString = value
      const coordinatesArray = extractCoordinates(coordinatesString)
      if (coordinatesArray.length === 2) {
        // Box query
        response.result.operator = 'GEO_BOX'
      } else if (coordinatesArray.length === 4) {
        // Polygon query
        response.result.operator = 'GEO_POLYGON'
      }
    }
  } else if (field === 'published') {
    if (operator === 'IS_ONE_OF' && _.isEqual(value, [true, false])) {
      response.result.value = 'Show all files (including unpublished files)'
    } else {
      response.result.value =
        value === false
          ? 'Only show unpublished files'
          : 'Only show published files'
    }
  } else if (field === 'transparency') {
    if (operator === 'IS_ONE_OF' && _.isEqual(value, [true, false])) {
      response.result.value = 'Show all files (include transparent files)'
    } else {
      response.result.value =
        value === true
          ? 'Only show files with transparency'
          : 'Only show files with no transparency'
    }
  } else if (field === 'private') {
    if (operator === 'IS_ONE_OF' && _.isEqual(value, [true, false])) {
      response.result.value = 'Show all files (include private files)'
    } else {
      response.result.value =
        value === true
          ? 'Only show private files'
          : 'Only show non-private files'
    }
  }

  // Handle embedded metadata and custom metadata fields
  if (
    field.includes('embeddedMetadata.') ||
    field.includes('customMetadata.')
  ) {
    response.result.fieldName = `"${field}"`
  }

  // Convert boolean values to strings for custom metadata Boolean fields
  // The form expects string values ("true"/"false") for booleanMapping lookup
  if (
    fieldType === 'Boolean' &&
    field.includes('customMetadata.') &&
    operator === 'IS' &&
    typeof value === 'boolean'
  ) {
    response.result.value = String(value)
  }

  return response
}

const flattenSearchQueryJson = (queryObject: any): any => {
  if (!queryObject || typeof queryObject !== 'object') {
    return queryObject
  }

  // If it's not a boolean expression node, return as-is
  if (!queryObject.boolean || !Array.isArray(queryObject.expressions)) {
    return queryObject
  }

  const isOrNode = queryObject.boolean === 'or'
  const isAndNode = queryObject.boolean === 'and'

  if (isOrNode || isAndNode) {
    // Recursively flatten all expressions, not just first 2
    const flattenedExpressions = queryObject.expressions
      .map((expr: any) => flattenSearchQueryJson(expr))
      .filter((expr: any) => expr !== null && expr !== undefined)

    // If no expressions after flattening, return empty structure
    if (flattenedExpressions.length === 0) {
      return {
        boolean: queryObject.boolean,
        expressions: []
      }
    }

    const newQueryObject: any = {
      boolean: queryObject.boolean,
      expressions: []
    }

    // Flatten nested boolean nodes of the same type
    flattenedExpressions.forEach((expr: any) => {
      if (expr && typeof expr === 'object' && expr.boolean === queryObject.boolean) {
        // Same boolean type - merge expressions
        if (Array.isArray(expr.expressions)) {
          newQueryObject.expressions.push(...expr.expressions)
        }
      } else if (expr) {
        // Different type or leaf node - add as-is
        newQueryObject.expressions.push(expr)
      }
    })

    if (newQueryObject.expressions.length === 1) {
      return newQueryObject.expressions[0]
    }  

    return newQueryObject
  }

  return queryObject
}

const getNestingLevel = (searchQueryJson: any): number => {
  if (!searchQueryJson.boolean) {
    return 0
  }
  if (searchQueryJson.expressions.every((exp: any) => !exp.boolean)) {
    return 1
  }
  const nestedLevels = searchQueryJson.expressions.map((exp: any) =>
    getNestingLevel(exp),
  )
  return 1 + Math.max(...nestedLevels)
}

// Check if height and width range expressions match a predefined dimension option
const matchesPredefinedDimension = (
  heightGteExpr: any,
  heightLteExpr: any,
  widthGteExpr: any,
  widthLteExpr: any,
): string | null => {
  if (!heightGteExpr || !heightLteExpr || !widthGteExpr || !widthLteExpr) return null

  const heightMin = parseInt(heightGteExpr.value, 10)
  const heightMax = parseInt(heightLteExpr.value, 10)
  const widthMin = parseInt(widthGteExpr.value, 10)
  const widthMax = parseInt(widthLteExpr.value, 10)

  // Check each predefined dimension option
  for (const [optionName, optionConfig] of Object.entries(dimensionOptions)) {
    // Check if this is a range option (has both min and max)
    const hasHeightRange = 'min' in optionConfig.height && 'max' in optionConfig.height
    const hasWidthRange = 'min' in optionConfig.width && 'max' in optionConfig.width

    if (hasHeightRange && hasWidthRange) {
      const heightMatches = 
        (optionConfig.height as any).min === heightMin && 
        (optionConfig.height as any).max === heightMax
      const widthMatches = 
        (optionConfig.width as any).min === widthMin && 
        (optionConfig.width as any).max === widthMax

      if (heightMatches && widthMatches) {
        return optionName
      }
    }
  }

  return null
}

// Check if single height/width expressions match a predefined dimension option
const matchesPredefinedDimensionSingle = (
  heightExpr: any,
  widthExpr: any,
): string | null => {
  if (!heightExpr || !widthExpr) return null

  const heightValue = parseInt(heightExpr.value, 10)
  const widthValue = parseInt(widthExpr.value, 10)
  const heightOp = heightExpr.operator
  const widthOp = widthExpr.operator

  // First, try to match 'Larger than' options (they have priority)
  const largerThanOptions = Object.entries(dimensionOptions).filter(
    ([name]) => name.startsWith('Larger than'),
  )

  for (const [optionName, optionConfig] of largerThanOptions) {
    const heightMatches =
      'min' in optionConfig.height &&
      optionConfig.height.min &&
      heightOp === 'GREATER_THAN_EQUAL' &&
      heightValue === optionConfig.height.min
    const widthMatches =
      'min' in optionConfig.width &&
      optionConfig.width.min &&
      widthOp === 'GREATER_THAN_EQUAL' &&
      widthValue === optionConfig.width.min

    if (heightMatches && widthMatches) {
      return optionName
    }
  }

  // Then check other options (Icons, Small, Medium, Large) - single value matches
  const otherOptions = Object.entries(dimensionOptions).filter(
    ([name]) => !name.startsWith('Larger than'),
  )

  for (const [optionName, optionConfig] of otherOptions) {
    let heightMatches = false
    let widthMatches = false

    // Check height match
    if (
      'min' in optionConfig.height &&
      optionConfig.height.min &&
      heightOp === 'GREATER_THAN_EQUAL' &&
      heightValue === optionConfig.height.min
    ) {
      heightMatches = true
    } else if (
      'max' in optionConfig.height &&
      optionConfig.height.max &&
      heightOp === 'LESS_THAN_EQUAL' &&
      heightValue === optionConfig.height.max
    ) {
      heightMatches = true
    }

    // Check width match
    if (
      'min' in optionConfig.width &&
      optionConfig.width.min &&
      widthOp === 'GREATER_THAN_EQUAL' &&
      widthValue === optionConfig.width.min
    ) {
      widthMatches = true
    } else if (
      'max' in optionConfig.width &&
      optionConfig.width.max &&
      widthOp === 'LESS_THAN_EQUAL' &&
      widthValue === optionConfig.width.max
    ) {
      widthMatches = true
    }

    if (heightMatches && widthMatches) {
      return optionName
    }
  }

  return null
}

// Helper function: Consolidate dimension fields (works with expressions array)
const consolidateDimensionFieldsOnExpressions = (expressions: any[]): any[] => {
  if (!Array.isArray(expressions) || expressions.length === 0) {
    return expressions
  }

  // Find all dimension expressions
  const heightGteExpressions = expressions.filter(
    expr => expr.fieldName === 'height' && expr.operator === 'GREATER_THAN_EQUAL',
  )
  const heightLteExpressions = expressions.filter(
    expr => expr.fieldName === 'height' && expr.operator === 'LESS_THAN_EQUAL',
  )
  const widthGteExpressions = expressions.filter(
    expr => expr.fieldName === 'width' && expr.operator === 'GREATER_THAN_EQUAL',
  )
  const widthLteExpressions = expressions.filter(
    expr => expr.fieldName === 'width' && expr.operator === 'LESS_THAN_EQUAL',
  )

  // Check if we can consolidate into predefined dimension option (range-based)
  if (heightGteExpressions.length === 1 && 
      heightLteExpressions.length === 1 && 
      widthGteExpressions.length === 1 && 
      widthLteExpressions.length === 1) {
    
    const matchedOption = matchesPredefinedDimension(
      heightGteExpressions[0],
      heightLteExpressions[0],
      widthGteExpressions[0],
      widthLteExpressions[0],
    )
    
    if (matchedOption) {
      // Create consolidated dimensions expression
      const consolidatedDimensionExpr = {
        fieldName: 'dimensions',
        operator: 'IS',
        value: matchedOption,
      }

      // Find the position of the first dimension expression to preserve order
      const firstDimensionIndex = expressions.findIndex(
        expr => (expr.fieldName === 'height' || expr.fieldName === 'width') && 
                (expr.operator === 'GREATER_THAN_EQUAL' || expr.operator === 'LESS_THAN_EQUAL')
      )

      // Remove the original height and width expressions
      const otherExpressions = expressions.filter(
        expr => !(
          (expr.fieldName === 'height' && (expr.operator === 'GREATER_THAN_EQUAL' || expr.operator === 'LESS_THAN_EQUAL')) ||
          (expr.fieldName === 'width' && (expr.operator === 'GREATER_THAN_EQUAL' || expr.operator === 'LESS_THAN_EQUAL'))
        ),
      )

      // Insert consolidated expression at the original position
      const newExpressions = [...otherExpressions]
      newExpressions.splice(firstDimensionIndex, 0, consolidatedDimensionExpr)

      return newExpressions
    }
  }

  // Check for single height/width expressions (existing logic)
  const heightExpressions = expressions.filter(expr => expr.fieldName === 'height')
  const widthExpressions = expressions.filter(expr => expr.fieldName === 'width')

  if (heightExpressions.length === 1 && widthExpressions.length === 1) {
    const heightExpr = heightExpressions[0]
    const widthExpr = widthExpressions[0]
    
    const matchedOption = matchesPredefinedDimensionSingle(heightExpr, widthExpr)
    
    if (matchedOption) {
      // Create consolidated dimensions expression
      const consolidatedDimensionExpr = {
        fieldName: 'dimensions',
        operator: 'IS',
        value: matchedOption,
      }

      // Find the position of the first dimension expression to preserve order
      const firstDimensionIndex = expressions.findIndex(
        expr => expr.fieldName === 'height' || expr.fieldName === 'width'
      )

      // Remove the original height and width expressions
      const otherExpressions = expressions.filter(
        expr => expr.fieldName !== 'height' && expr.fieldName !== 'width',
      )

      // Insert consolidated expression at the original position
      const newExpressions = [...otherExpressions]
      newExpressions.splice(firstDimensionIndex, 0, consolidatedDimensionExpr)

      return newExpressions
    }
  }

  // If no predefined match, try to consolidate GREATER_THAN_EQUAL expressions into CUSTOM_DIMENSIONS
  const gteHeightExpressions = heightExpressions.filter(
    expr => expr.operator === 'GREATER_THAN_EQUAL',
  )
  const gteWidthExpressions = widthExpressions.filter(
    expr => expr.operator === 'GREATER_THAN_EQUAL',
  )

  if (gteHeightExpressions.length > 0 || gteWidthExpressions.length > 0) {
    const consolidatedDimensionExpr = {
      fieldName: 'dimensions',
      operator: 'CUSTOM_DIMENSIONS',
      value: {
        height: { min: '' },
        width: { min: '' },
      },
    }

    // Process GREATER_THAN_EQUAL height expressions
    gteHeightExpressions.forEach(expr => {
      const value = parseInt(expr.value, 10)
      consolidatedDimensionExpr.value.height.min = value.toString()
    })

    // Process GREATER_THAN_EQUAL width expressions
    gteWidthExpressions.forEach(expr => {
      const value = parseInt(expr.value, 10)
      consolidatedDimensionExpr.value.width.min = value.toString()
    })

    // Remove empty dimension objects
    if (consolidatedDimensionExpr.value.height.min === '') {
      delete (consolidatedDimensionExpr.value as any).height
    }
    if (consolidatedDimensionExpr.value.width.min === '') {
      delete (consolidatedDimensionExpr.value as any).width
    }

    // Only consolidate if we have at least one dimension constraint
    if (Object.keys(consolidatedDimensionExpr.value).length > 0) {
      // Find the position of the first GREATER_THAN_EQUAL dimension expression to preserve order
      const firstGteDimensionIndex = expressions.findIndex(
        expr => (expr.fieldName === 'height' || expr.fieldName === 'width') && 
                expr.operator === 'GREATER_THAN_EQUAL'
      )

      // Remove the GREATER_THAN_EQUAL height and width expressions
      const otherExpressions = expressions.filter(
        expr =>
          !(
            (expr.fieldName === 'height' &&
              expr.operator === 'GREATER_THAN_EQUAL') ||
            (expr.fieldName === 'width' &&
              expr.operator === 'GREATER_THAN_EQUAL')
          ),
      )

      // Insert consolidated expression at the original position
      const newExpressions = [...otherExpressions]
      newExpressions.splice(firstGteDimensionIndex, 0, consolidatedDimensionExpr)

      return newExpressions
    }
  }

  return expressions
}

// Consolidate dimension fields function (backward compatibility - works with filterGroups)
const consolidateDimensionFields = (nestedJson: any): any => {
  if (!nestedJson?.filterGroups?.[0]?.expressions) {
    return nestedJson
  }

  const newExpressions = consolidateDimensionFieldsOnExpressions(nestedJson.filterGroups[0].expressions)

  return {
    ...nestedJson,
    filterGroups: [
      {
        ...nestedJson.filterGroups[0],
        expressions: newExpressions,
      },
    ],
  }
}

// Helper function: Check if dimension expressions should go to query mode (works with expressions array)
const shouldUseQueryModeForDimensionsOnExpressions = (expressions: any[]): boolean => {
  if (!Array.isArray(expressions) || expressions.length === 0) {
    return false
  }
  const heightExpressions = expressions.filter(
    expr => expr.fieldName === 'height',
  )
  const widthExpressions = expressions.filter(
    expr => expr.fieldName === 'width',
  )

  // If we have height/width expressions that don't match predefined options
  if (heightExpressions.length > 0 || widthExpressions.length > 0) {
    // Check for range-based consolidation (4 expressions)
    if (heightExpressions.length === 2 && widthExpressions.length === 2) {
      const heightGte = heightExpressions.find(expr => expr.operator === 'GREATER_THAN_EQUAL')
      const heightLte = heightExpressions.find(expr => expr.operator === 'LESS_THAN_EQUAL')
      const widthGte = widthExpressions.find(expr => expr.operator === 'GREATER_THAN_EQUAL')
      const widthLte = widthExpressions.find(expr => expr.operator === 'LESS_THAN_EQUAL')
      
      if (heightGte && heightLte && widthGte && widthLte) {
        const matchedOption = matchesPredefinedDimension(heightGte, heightLte, widthGte, widthLte)
        if (matchedOption) {
          return false // Can be consolidated
        }
      }
    }

    // Check for single expression consolidation
    if (heightExpressions.length === 1 && widthExpressions.length === 1) {
      const matchedOption = matchesPredefinedDimensionSingle(
        heightExpressions[0],
        widthExpressions[0],
      )
      if (matchedOption) {
        return false // Can be consolidated
      }
    }

    // Check if they are all GREATER_THAN_EQUAL (can be consolidated to CUSTOM_DIMENSIONS)
    const allGte = [...heightExpressions, ...widthExpressions].every(
      expr => expr.operator === 'GREATER_THAN_EQUAL',
    )

    if (allGte) {
      return false // Can be consolidated
    }

    // Otherwise, send to query mode
    return true
  }

  return false
}

// Check if dimension expressions should go to query mode (backward compatibility - works with filterGroups)
const shouldUseQueryModeForDimensions = (nestedJson: any): boolean => {
  if (!nestedJson?.filterGroups?.[0]?.expressions) {
    return false
  }

  return shouldUseQueryModeForDimensionsOnExpressions(nestedJson.filterGroups[0].expressions)
}

// Helper function: Consolidate size fields (works with expressions array)
const consolidateSizeFieldsOnExpressions = (expressions: any[]): any[] => {
  if (!Array.isArray(expressions) || expressions.length === 0) {
    return expressions
  }

  const sizeExpressions = expressions.filter(
    expr =>
      expr.fieldName === 'size' &&
      (expr.operator === 'GREATER_THAN_EQUAL' ||
        expr.operator === 'LESS_THAN_EQUAL'),
  )

  if (sizeExpressions.length === 2) {
    const [gte, lte] = sizeExpressions.sort((a, _b) =>
      a.operator === 'GREATER_THAN_EQUAL' ? -1 : 1,
    )

    // Extract unit and values
    const gteValue = gte.value
    const lteValue = lte.value

    // Handle both cases: "1024" (bytes) and "1024KB" (with unit)
    const gteMatch = gteValue.match(/(\d+)([A-Z]+)?/)
    const lteMatch = lteValue.match(/(\d+)([A-Z]+)?/)

    const gteUnit = gteMatch?.[2] || 'B' // Default to 'B' if no unit
    const lteUnit = lteMatch?.[2] || 'B' // Default to 'B' if no unit

    if (gteUnit === lteUnit) {
      const minValue = parseInt(gteMatch?.[1] || '0', 10)
      const maxValue = parseInt(lteMatch?.[1] || '0', 10)

      // Create consolidated size expression
      const consolidatedSizeExpr = {
        fieldName: 'size',
        operator: 'SLIDER',
        value: {
          unit: gteUnit,
          range: {
            min: minValue,
            max: maxValue,
          },
        },
      }

      // Find the position of the first size expression to preserve order
      const firstSizeIndex = expressions.findIndex(
        expr =>
          expr.fieldName === 'size' &&
          (expr.operator === 'GREATER_THAN_EQUAL' ||
            expr.operator === 'LESS_THAN_EQUAL')
      )

      // Remove the size expressions
      const otherExpressions = expressions.filter(
        expr =>
          !(
            expr.fieldName === 'size' &&
            (expr.operator === 'GREATER_THAN_EQUAL' ||
              expr.operator === 'LESS_THAN_EQUAL')
          ),
      )

      // Insert consolidated expression at the original position
      const newExpressions = [...otherExpressions]
      newExpressions.splice(firstSizeIndex, 0, consolidatedSizeExpr)

      return newExpressions
    }
  }

  return expressions
}

// Helper function: Check if a field is a date field
const isDateField = (
  fieldName: string,
  customMetadataFields?: CmFieldResponseObject[],
): boolean => {
  // Known date fields
  const knownDateFields = [
    'createdAt',
    'updatedAt',
    'embeddedMetadata.DateTimeOriginal',
  ]
  
  if (knownDateFields.includes(fieldName)) {
    return true
  }
  
  // Check if it's a custom metadata date field
  // Handle both quoted and unquoted field names: "customMetadata.field" or customMetadata.field
  if (fieldName.includes('customMetadata.')) {
    if (customMetadataFields) {
      // Remove quotes if present, then remove customMetadata. prefix
      const unquotedFieldName = fieldName.replace(/^"/, '').replace(/"$/, '')
      const customFieldName = unquotedFieldName.replace('customMetadata.', '')
      const customField = customMetadataFields.find(
        field => field.name === customFieldName,
      )
      if (customField?.schema?.type === 'Date') {
        return true
      }
    }
  }
  
  return false
}

// Helper function: Check if a field is a number field
const isNumberField = (
  fieldName: string,
  customMetadataFields?: CmFieldResponseObject[],
): boolean => {
  // Known number fields (but these don't support BETWEEN - only custom metadata do)
  const knownNumberFields = ['height', 'width']
  
  if (knownNumberFields.includes(fieldName)) {
    return true
  }
  
  // Check if it's a custom metadata number field
  // Handle both quoted and unquoted field names: "customMetadata.field" or customMetadata.field
  if (fieldName.includes('customMetadata.')) {
    if (customMetadataFields) {
      // Remove quotes if present, then remove customMetadata. prefix
      const unquotedFieldName = fieldName.replace(/^"/, '').replace(/"$/, '')
      const customFieldName = unquotedFieldName.replace('customMetadata.', '')
      const customField = customMetadataFields.find(
        field => field.name === customFieldName,
      )
      if (customField?.schema?.type === 'Number') {
        return true
      }
    }
  }
  
  return false
}

// Helper function: Check if a date matches LAST_7_DAYS or LAST_30_DAYS pattern
const getLastDaysOperator = (dateValue: Date | string): 'LAST_7_DAYS' | 'LAST_30_DAYS' | null => {
  const dateMoment = moment(dateValue)

  if (!dateMoment.isValid()) {
    return null
  }

  // Calculate 7 and 30 days ago (start of day) - matching the pattern in mediaLibrary.tsx
  // moment().subtract(7, 'days').startOf('day').toISOString()
  // This uses local timezone for startOf('day'), then converts to UTC ISO string
  const sevenDaysAgoISO = moment().subtract(7, 'days').startOf('day').toISOString()
  const thirtyDaysAgoISO = moment().subtract(30, 'days').startOf('day').toISOString()
  
  // Convert the input date to ISO string for comparison
  const dateISO = dateMoment.toISOString()

  // Compare ISO strings directly (exact match required)
  if (dateISO === sevenDaysAgoISO) {
    return 'LAST_7_DAYS'
  }
  if (dateISO === thirtyDaysAgoISO) {
    return 'LAST_30_DAYS'
  }

  return null
}

// Helper function: Check if two dates are on the same day and represent start/end of day
// EXACTLY generates: fieldName > "startOfDay" AND fieldName <= "endOfDay"
// So LATER_THAN value should be start of day, LESS_THAN_EQUAL value should be end of day
const areDatesOnSameDayForExactly = (
  laterThanValue: Date | string,
  lessThanEqualValue: Date | string,
): boolean => {
  const laterThanMoment = moment(laterThanValue)
  const lessThanEqualMoment = moment(lessThanEqualValue)
  
  if (!laterThanMoment.isValid() || !lessThanEqualMoment.isValid()) {
    return false
  }
  
  // Check if they are on the same calendar day
  const sameDay =
    laterThanMoment.year() === lessThanEqualMoment.year() &&
    laterThanMoment.month() === lessThanEqualMoment.month() &&
    laterThanMoment.date() === lessThanEqualMoment.date()
  
  if (!sameDay) {
    return false
  }
  
  // Verify that LATER_THAN value represents start of day (00:00:00.000)
  // When EXACTLY is generated, it uses: fieldName > "startOfDay"
  // This becomes LATER_THAN with value = startOfDay
  const laterThanStartOfDay = laterThanMoment.clone().startOf('day')
  const laterThanIsStartOfDay = laterThanMoment.isSame(laterThanStartOfDay, 'minute')
  
  // Verify that LESS_THAN_EQUAL value represents end of day (23:59:59.999)
  // When EXACTLY is generated, it uses: fieldName <= "endOfDay"
  // This becomes LESS_THAN_EQUAL with value = endOfDay
  const lessThanEqualEndOfDay = lessThanEqualMoment.clone().endOf('day')
  const lessThanEqualIsEndOfDay = lessThanEqualMoment.isSame(lessThanEqualEndOfDay, 'minute')
  
  // LATER_THAN must be start of day, LESS_THAN_EQUAL must be end of day
  // This ensures it represents a full day (EXACTLY pattern)
  return laterThanIsStartOfDay && lessThanEqualIsEndOfDay
}

// Helper function: Check if a date represents start of day (00:00:00.000)
const isStartOfDay = (dateValue: Date | string): boolean => {
  const dateMoment = moment(dateValue)
  if (!dateMoment.isValid()) {
    return false
  }
  const startOfDay = dateMoment.clone().startOf('day')
  return dateMoment.isSame(startOfDay, 'minute')
}

// Helper function: Check if a date represents end of day (23:59:59.999)
const isEndOfDay = (dateValue: Date | string): boolean => {
  const dateMoment = moment(dateValue)
  if (!dateMoment.isValid()) {
    return false
  }
  const endOfDay = dateMoment.clone().endOf('day')
  return dateMoment.isSame(endOfDay, 'minute')
}

// Helper function: Consolidate CUSTOM_RANGE date fields (pair of LATER_THAN + LESS_THAN_EQUAL)
const consolidateCustomRangeFieldsOnExpressions = (
  expressions: any[],
  customMetadataFields?: CmFieldResponseObject[],
): any[] => {
  if (!Array.isArray(expressions) || expressions.length === 0) {
    return expressions
  }

  let consolidatedExpressions = [...expressions]
  const fieldsToConsolidate: Array<{ fieldName: string; firstIndex: number; consolidatedExpr: any }> = []

  // Group expressions by field name and check for date fields
  const fieldGroups = new Map<string, any[]>()
  
  consolidatedExpressions.forEach(expr => {
    if (
      isDateField(expr.fieldName, customMetadataFields) &&
      (expr.operator === 'LATER_THAN' || expr.operator === 'LESS_THAN_EQUAL')
    ) {
      const fieldName = expr.fieldName
      if (!fieldGroups.has(fieldName)) {
        fieldGroups.set(fieldName, [])
      }
      fieldGroups.get(fieldName)!.push(expr)
    }
  })
  
  // Check for CUSTOM_RANGE patterns (pair of LATER_THAN + LESS_THAN_EQUAL)
  for (const [fieldName, dateExpressions] of fieldGroups) {
    const greaterThanExpr = dateExpressions.find(expr => expr.operator === 'LATER_THAN')
    const lessThanEqualExpr = dateExpressions.find(expr => expr.operator === 'LESS_THAN_EQUAL')

    if (greaterThanExpr && lessThanEqualExpr && dateExpressions.length === 2) {
      // Extract date values - handle both { exact: Date } structures and direct Date objects
      const fromValue = greaterThanExpr.value?.exact || greaterThanExpr.value
      const toValue = lessThanEqualExpr.value?.exact || lessThanEqualExpr.value

      // Validate that LATER_THAN value is start of day and LESS_THAN_EQUAL value is end of day
      // CUSTOM_RANGE should have: from = start of day (00:00:00.000), to = end of day (23:59:59.999)
      if (!isStartOfDay(fromValue) || !isEndOfDay(toValue)) {
        // Skip consolidation if dates don't match start/end of day boundaries
        continue
      }

      // Create consolidated date range expression
      const consolidatedDateRangeExpr = {
        fieldName: fieldName,
        operator: 'CUSTOM_RANGE',
        value: {
          from: fromValue,
          to: toValue,
        },
      }

      // Find the position of the first date expression to preserve order
      const firstDateIndex = consolidatedExpressions.findIndex(
        expr =>
          expr.fieldName === fieldName &&
          (expr.operator === 'LATER_THAN' || expr.operator === 'LESS_THAN_EQUAL'),
      )

      fieldsToConsolidate.push({
        fieldName,
        firstIndex: firstDateIndex,
        consolidatedExpr: consolidatedDateRangeExpr,
      })
    }
  }

  // Apply CUSTOM_RANGE consolidations in reverse order of index to preserve positions
  fieldsToConsolidate
    .sort((a, b) => b.firstIndex - a.firstIndex)
    .forEach(({ fieldName, firstIndex, consolidatedExpr }) => {
      // Remove the date expressions for this field
      consolidatedExpressions = consolidatedExpressions.filter(
        expr =>
          !(
            expr.fieldName === fieldName &&
            (expr.operator === 'LATER_THAN' || expr.operator === 'LESS_THAN_EQUAL')
          ),
      )

      // Insert consolidated expression at the original position
      consolidatedExpressions.splice(firstIndex, 0, consolidatedExpr)
    })

  return consolidatedExpressions
}

// Helper function: Consolidate LAST_7_DAYS/LAST_30_DAYS date fields (single LATER_THAN)
const consolidateLastDaysFieldsOnExpressions = (
  expressions: any[],
  customMetadataFields?: CmFieldResponseObject[],
): any[] => {
  if (!Array.isArray(expressions) || expressions.length === 0) {
    return expressions
  }

  const consolidatedExpressions = [...expressions]
  const lastDaysToConsolidate: Array<{ index: number; consolidatedExpr: any }> = []

  // Check for LAST_7_DAYS/LAST_30_DAYS patterns (single LATER_THAN)
  consolidatedExpressions.forEach((expr, index) => {
    if (
      isDateField(expr.fieldName, customMetadataFields) &&
      expr.operator === 'LATER_THAN'
    ) {
      // Extract date value
      const dateValue = expr.value?.exact || expr.value
      const lastDaysOperator = getLastDaysOperator(dateValue)

      if (lastDaysOperator) {
        // Create consolidated LAST_7_DAYS or LAST_30_DAYS expression
        const consolidatedLastDaysExpr = {
          fieldName: expr.fieldName,
          operator: lastDaysOperator,
          value: {
            exact: '',
          },
        }

        lastDaysToConsolidate.push({
          index,
          consolidatedExpr: consolidatedLastDaysExpr,
        })
      }
    }
  })

  // Apply LAST_7_DAYS/LAST_30_DAYS consolidations in reverse order of index
  lastDaysToConsolidate
    .sort((a, b) => b.index - a.index)
    .forEach(({ index, consolidatedExpr }) => {
      consolidatedExpressions[index] = consolidatedExpr
    })

  return consolidatedExpressions
}

// Helper function: Consolidate EXACTLY date fields (LATER_THAN + LESS_THAN_EQUAL on same day)
const consolidateExactlyFieldsOnExpressions = (
  expressions: any[],
  customMetadataFields?: CmFieldResponseObject[],
): any[] => {
  if (!Array.isArray(expressions) || expressions.length === 0) {
    return expressions
  }

  let consolidatedExpressions = [...expressions]
  const fieldsToConsolidate: Array<{ fieldName: string; firstIndex: number; consolidatedExpr: any }> = []

  // Group expressions by field name and check for date fields
  const fieldGroups = new Map<string, any[]>()
  
  consolidatedExpressions.forEach(expr => {
    if (
      isDateField(expr.fieldName, customMetadataFields) &&
      (expr.operator === 'LATER_THAN' || expr.operator === 'LESS_THAN_EQUAL')
    ) {
      const fieldName = expr.fieldName
      if (!fieldGroups.has(fieldName)) {
        fieldGroups.set(fieldName, [])
      }
      fieldGroups.get(fieldName)!.push(expr)
    }
  })
  
  // Check for EXACTLY patterns (pair of LATER_THAN + LESS_THAN_EQUAL on same day)
  for (const [fieldName, dateExpressions] of fieldGroups) {
    const laterThanExpr = dateExpressions.find(expr => expr.operator === 'LATER_THAN')
    const lessThanEqualExpr = dateExpressions.find(expr => expr.operator === 'LESS_THAN_EQUAL')

    if (laterThanExpr && lessThanEqualExpr && dateExpressions.length === 2) {
      // Extract date values - handle both { exact: Date } structures and direct Date objects
      const laterThanValue = laterThanExpr.value?.exact || laterThanExpr.value
      const lessThanEqualValue = lessThanEqualExpr.value?.exact || lessThanEqualExpr.value
      
      // Check if dates are on the same day AND represent start/end of day (full day)
      if (areDatesOnSameDayForExactly(laterThanValue, lessThanEqualValue)) {
        // Create consolidated EXACTLY expression
        // Use the start of day value (LATER_THAN value) as the exact value
        // This represents the date for the EXACTLY operator
        const consolidatedExactlyExpr = {
          fieldName: fieldName,
          operator: 'EXACTLY',
          value: {
            exact: laterThanValue, // This is start of day, which represents the date
          },
        }

        // Find the position of the first date expression to preserve order
        const firstDateIndex = consolidatedExpressions.findIndex(
          expr =>
            expr.fieldName === fieldName &&
            (expr.operator === 'LATER_THAN' || expr.operator === 'LESS_THAN_EQUAL'),
        )

        fieldsToConsolidate.push({
          fieldName,
          firstIndex: firstDateIndex,
          consolidatedExpr: consolidatedExactlyExpr,
        })
      }
    }
  }

  // Apply EXACTLY consolidations in reverse order of index to preserve positions
  fieldsToConsolidate
    .sort((a, b) => b.firstIndex - a.firstIndex)
    .forEach(({ fieldName, firstIndex, consolidatedExpr }) => {
      // Remove the date expressions for this field
      consolidatedExpressions = consolidatedExpressions.filter(
        expr =>
          !(
            expr.fieldName === fieldName &&
            (expr.operator === 'LATER_THAN' || expr.operator === 'LESS_THAN_EQUAL')
          ),
      )

      // Insert consolidated expression at the original position
      consolidatedExpressions.splice(firstIndex, 0, consolidatedExpr)
    })

  return consolidatedExpressions
}

// Helper function: Consolidate BETWEEN number fields (GREATER_THAN_EQUAL + LESS_THAN_EQUAL)
// Only for custom metadata Number fields (not standard fields like height/width)
const consolidateBetweenFieldsOnExpressions = (
  expressions: any[],
  customMetadataFields?: CmFieldResponseObject[],
): any[] => {
  if (!Array.isArray(expressions) || expressions.length === 0) {
    return expressions
  }

  let consolidatedExpressions = [...expressions]
  const fieldsToConsolidate: Array<{ fieldName: string; firstIndex: number; consolidatedExpr: any }> = []

  // Group expressions by field name and check for custom metadata number fields only
  const fieldGroups = new Map<string, any[]>()
  
  consolidatedExpressions.forEach(expr => {
    // Only process custom metadata number fields (exclude standard fields like height/width)
    // Handle both quoted and unquoted field names: "customMetadata.field" or customMetadata.field
    if (
      expr.fieldName.includes('customMetadata.') &&
      isNumberField(expr.fieldName, customMetadataFields) &&
      (expr.operator === 'GREATER_THAN_EQUAL' || expr.operator === 'LESS_THAN_EQUAL')
    ) {
      const fieldName = expr.fieldName
      if (!fieldGroups.has(fieldName)) {
        fieldGroups.set(fieldName, [])
      }
      fieldGroups.get(fieldName)!.push(expr)
    }
  })
  
  // Check for BETWEEN patterns (pair of GREATER_THAN_EQUAL + LESS_THAN_EQUAL)
  for (const [fieldName, numberExpressions] of fieldGroups) {
    const gteExpr = numberExpressions.find(expr => expr.operator === 'GREATER_THAN_EQUAL')
    const lteExpr = numberExpressions.find(expr => expr.operator === 'LESS_THAN_EQUAL')

    if (gteExpr && lteExpr && numberExpressions.length === 2) {
      // Extract number values
      const minValue = gteExpr.value
      const maxValue = lteExpr.value
      
      // Validate that min <= max
      const minNum = typeof minValue === 'string' ? parseFloat(minValue) : minValue
      const maxNum = typeof maxValue === 'string' ? parseFloat(maxValue) : maxValue
      
      if (!isNaN(minNum) && !isNaN(maxNum) && minNum <= maxNum) {
        // Create consolidated BETWEEN expression
        const consolidatedBetweenExpr = {
          fieldName: fieldName,
          operator: 'BETWEEN',
          value: {
            min: minValue.toString(),
            max: maxValue.toString(),
          },
        }

        // Find the position of the first number expression to preserve order
        const firstNumberIndex = consolidatedExpressions.findIndex(
          expr =>
            expr.fieldName === fieldName &&
            (expr.operator === 'GREATER_THAN_EQUAL' || expr.operator === 'LESS_THAN_EQUAL'),
        )

        fieldsToConsolidate.push({
          fieldName,
          firstIndex: firstNumberIndex,
          consolidatedExpr: consolidatedBetweenExpr,
        })
      }
    }
  }

  // Apply BETWEEN consolidations in reverse order of index to preserve positions
  fieldsToConsolidate
    .sort((a, b) => b.firstIndex - a.firstIndex)
    .forEach(({ fieldName, firstIndex, consolidatedExpr }) => {
      // Remove the number expressions for this field
      consolidatedExpressions = consolidatedExpressions.filter(
        expr =>
          !(
            expr.fieldName === fieldName &&
            (expr.operator === 'GREATER_THAN_EQUAL' || expr.operator === 'LESS_THAN_EQUAL')
          ),
      )

      // Insert consolidated expression at the original position
      consolidatedExpressions.splice(firstIndex, 0, consolidatedExpr)
    })

  return consolidatedExpressions
}

// Consolidate size fields function moved from MLProvider.tsx (backward compatibility - works with filterGroups)
const consolidateSizeFields = (nestedJson: any): any => {
  // Check if the structure has filterGroups and expressions
  if (!nestedJson?.filterGroups?.[0]?.expressions) {
    return nestedJson
  }

  const newExpressions = consolidateSizeFieldsOnExpressions(nestedJson.filterGroups[0].expressions)

  return {
    ...nestedJson,
    filterGroups: [
      {
        ...nestedJson.filterGroups[0],
        expressions: newExpressions,
      },
    ],
  }
}

// Main function to parse search query with validation
export const parseSearchQueryWithValidation = (
  searchQuery: string,
  customMetadataFields: CmFieldResponseObject[],
) => {
  try {
    return searchQueryJsonParser(searchQuery, {
      validate: ({ field, operator, value }) =>
        validateSearchFilters(
          { field, operator, value },
          { customMetadataFields },
        ),
    })
  } catch (e) {
    return null
  }
}

// Helper function: Consolidate all expressions (size + dimensions)
const consolidateExpressions = (
  flattenedJson: any,
  customMetadataFields?: CmFieldResponseObject[],
): { boolean: string; expressions: any[] } => {
  // Handle nesting level 0 case (single expression without boolean wrapper)
  // Normalize to boolean structure
  let normalizedJson: { boolean: string; expressions: any[] }
  
  if (!flattenedJson.boolean) {
    // Single expression - wrap it in a boolean structure
    normalizedJson = {
      boolean: 'and',
      expressions: [flattenedJson]
    }
  } else {
    // Already a boolean structure
    normalizedJson = flattenedJson
  }

  const expressions = normalizedJson.expressions || []

  // 1. Check dimension mode requirements first (fail fast)
  if (shouldUseQueryModeForDimensionsOnExpressions(expressions)) {
    throw new Error('Unsupported dimension operators detected - use query mode')
  }

  // 2. Consolidate size fields (returns modified expressions)
  let consolidatedExpressions = consolidateSizeFieldsOnExpressions(expressions)

  // 3. Consolidate BETWEEN number fields (must be before dimensions to avoid conflicts)
  // Only for custom metadata Number fields
  consolidatedExpressions = consolidateBetweenFieldsOnExpressions(
    consolidatedExpressions,
    customMetadataFields,
  )

  // 4. Consolidate dimension fields (returns modified expressions)
  consolidatedExpressions = consolidateDimensionFieldsOnExpressions(consolidatedExpressions)

  // 5. Consolidate EXACTLY date fields (must be BEFORE CUSTOM_RANGE)
  // EXACTLY is a special case where from and to are the same day
  consolidatedExpressions = consolidateExactlyFieldsOnExpressions(
    consolidatedExpressions,
    customMetadataFields,
  )

  // 6. Consolidate CUSTOM_RANGE date fields (returns modified expressions)
  consolidatedExpressions = consolidateCustomRangeFieldsOnExpressions(
    consolidatedExpressions,
    customMetadataFields,
  )

  // 7. Consolidate LAST_7_DAYS/LAST_30_DAYS date fields (returns modified expressions)
  consolidatedExpressions = consolidateLastDaysFieldsOnExpressions(
    consolidatedExpressions,
    customMetadataFields,
  )

  // 8. Validate that no date fields have unsupported operators after consolidation
  const unsupportedDateOperators = ['LESS_THAN_EQUAL', 'GREATER_THAN_EQUAL']
  for (const expr of consolidatedExpressions) {
    if (
      isDateField(expr.fieldName, customMetadataFields) &&
      unsupportedDateOperators.includes(expr.operator)
    ) {
      throw new Error(
        `Unsupported operator '${expr.operator}' for date field '${expr.fieldName}'. Date range filters must use CUSTOM_RANGE operator.`,
      )
    }
  }

  // 7. Return updated structure
  return {
    ...normalizedJson,
    expressions: consolidatedExpressions,
  }
}

// Function to wrap flattened nested JSON in filterGroups structure
const wrapInFilterGroups = (flattenedJson: {
  boolean: string
  expressions: any[]
}): { boolean: string; filterGroups: any[] } | null => {
  if (!flattenedJson) {
    return null
  }

  // Check if flattenedJson has empty expressions array
  const hasEmptyExpressions =
    Array.isArray(flattenedJson.expressions) &&
    flattenedJson.expressions.length === 0

  // If expressions are empty, return empty filterGroups
  if (hasEmptyExpressions) {
    return {
      boolean: 'and',
      filterGroups: [],
    }
  }

  const nestingLevel = getNestingLevel(flattenedJson)

  // Only support case 0 (single level)
  if (nestingLevel === 0) {
    return {
      boolean: 'and',
      filterGroups: [
        {
          boolean: 'and',
          expressions: [flattenedJson],
        },
      ],
    }
  } else if (nestingLevel === 1) {
    return {
      boolean: 'and',
      filterGroups: [flattenedJson],
    }
  }

  // Throw error for all other nesting levels
  throw new Error(
    `Nesting level ${nestingLevel} is not supported. Only single level queries are supported.`,
  )
}

// Function to convert parsed query to the format expected by MLProvider (backward compatibility)
export const convertToSearchQueryJson = (searchQueryJson: any) => {
  if (!searchQueryJson) {
    return null
  }

  const flattenedJson = flattenSearchQueryJson(searchQueryJson)
  return wrapInFilterGroups(flattenedJson)
}

// Combined function that handles parsing, consolidation, and conversion
export const parseAndConvertSearchQuery = (
  searchQuery: string,
  customMetadataFields: CmFieldResponseObject[],
) => {
  // Step 1: Parse the search query with validation
  const nestedJson = parseSearchQueryWithValidation(
    searchQuery,
    customMetadataFields,
  )
  if (!nestedJson) {
    throw new Error('Failed to parse search query')
  }

  // Step 2: Flatten the nested JSON structure
  const flattenedJson = flattenSearchQueryJson(nestedJson)

  // Step 3: Check nesting level early (before any processing)
  const nestingLevel = getNestingLevel(flattenedJson)
  if (nestingLevel > 1) {
    throw new Error(
      `Nesting level ${nestingLevel} is not supported. Only single level queries are supported.`,
    )
  }

  // Step 4: Check for OR early (before wrapping)
  if (containsOr(flattenedJson)) {
    throw new Error('Complex query detected - use query mode')
  }

  // Step 5: Check for type field early
  if (hasTypeField(flattenedJson)) {
    throw new Error('Type field is not supported in builder mode - use query mode')
  }

  // Step 6: Consolidate all expressions (size + dimensions)
  // This function handles:
  //   - Dimension mode check → throws if unsupported
  //   - Size field consolidation
  //   - Dimension field consolidation
  const consolidatedJson = consolidateExpressions(flattenedJson, customMetadataFields)

  // Step 7: Check for duplicate fields (after consolidation)
  if (containsDuplicateField(consolidatedJson)) {
    throw new Error('Complex query detected - use query mode')
  }

  // Step 8: Wrap in filterGroups structure (final format conversion)
  const result = wrapInFilterGroups(consolidatedJson)
  if (!result) {
    throw new Error('Failed to wrap search query in filterGroups')
  }

  // Step 9: Return the final result
  return result
}

// Helper function to reverse map formats array to FileType
const reverseMapFormatsToFileType = (
  formats: string[],
  operator: string,
): FileType | '' => {
  if (!Array.isArray(formats) || formats.length === 0) {
    return ''
  }

  // Sort both arrays for comparison (order doesn't matter)
  const sortedFormats = [...formats].sort()
  
  // Check for IS_ONE_OF (IN operator)
  if (operator === 'IS_ONE_OF') {
    // Check images
    const sortedImages = [...fileTypeMapping.images].sort()
    if (sortedFormats.length === sortedImages.length && 
        sortedFormats.every((val, idx) => val === sortedImages[idx])) {
      return FileType.images
    }
    
    // Check videos
    const sortedVideos = [...fileTypeMapping.videos].sort()
    if (sortedFormats.length === sortedVideos.length && 
        sortedFormats.every((val, idx) => val === sortedVideos[idx])) {
      return FileType.videos
    }
    
    // Check cssJs
    const sortedCssJs = [...fileTypeMapping.cssJs].sort()
    if (sortedFormats.length === sortedCssJs.length && 
        sortedFormats.every((val, idx) => val === sortedCssJs[idx])) {
      return FileType.cssJs
    }
  }
  
  // Check for IS_NONE_OF (NOT IN operator) - maps to others
  if (operator === 'IS_NONE_OF') {
    const sortedOthers = [...fileTypeMapping.others].sort()
    if (sortedFormats.length === sortedOthers.length && 
        sortedFormats.every((val, idx) => val === sortedOthers[idx])) {
      return FileType.others
    }
  }
  
  return ''
}

// Modified version for auto-completer modal that handles special OR cases
export const parseAndConvertSearchQueryForAutoCompleter = (
  searchQuery: string,
  customMetadataFields: CmFieldResponseObject[],
) => {
  // Step 1: Parse the search query with validation
  const nestedJson = parseSearchQueryWithValidation(
    searchQuery,
    customMetadataFields,
  )
  if (!nestedJson) {
    throw new Error('Failed to parse search query')
  }

  // Step 2: Flatten the nested JSON structure
  const flattenedJson = flattenSearchQueryJson(nestedJson)

  // Step 3: Check for type field early
  if (hasTypeField(flattenedJson)) {
    throw new Error(
      'Type field is not supported in builder mode - use query mode',
    )
  }

  // Step 4: Extract special OR pattern: name HAS "value" OR tags HAS "value"
  // This happens before wrapping, working on flattened structure
  let extractedQ = ''
  let modifiedFlattenedJson = flattenedJson
  let modifiedSearchQuery = searchQuery

  // Check if there's an OR expression at level 0, 1, or 2
  const hasOrAtTopLevel = flattenedJson.boolean === 'or'
  const hasOrAtFirstLevel = flattenedJson.expressions?.some(
    expr => typeof expr === 'object' && expr.boolean === 'or'
  )

  // Check for OR expressions within individual expressions
  const hasOrInExpressions = flattenedJson.expressions?.some(expr => 
    typeof expr === 'object' && expr.expressions?.some(subExpr => 
      typeof subExpr === 'object' && subExpr.boolean === 'or'
    )
  )

  if (hasOrAtTopLevel || hasOrAtFirstLevel || hasOrInExpressions) {
    // Find the OR expression
    let orExpression: any = null
    let orExpressionIndex = -1
    let parentIndex = -1
    
    if (hasOrAtTopLevel) {
      orExpression = flattenedJson
    } else if (hasOrAtFirstLevel) {
      const expr = flattenedJson.expressions?.find(expr => 
        typeof expr === 'object' && expr.boolean === 'or'
      )
      orExpression = expr
    } else if (hasOrInExpressions) {
      // Find OR within expressions
      for (let i = 0; i < (flattenedJson.expressions?.length || 0); i++) {
        const expr = flattenedJson.expressions?.[i]
        if (typeof expr === 'object' && expr.expressions) {
          for (let j = 0; j < expr.expressions.length; j++) {
            const subExpr = expr.expressions[j]
            if (typeof subExpr === 'object' && subExpr.boolean === 'or') {
              orExpression = subExpr
              orExpressionIndex = j
              parentIndex = i
              break
            }
          }
        }
        if (orExpression) break
      }
    }

    if (orExpression && orExpression.expressions?.length === 2) {
      const [expr1, expr2] = orExpression.expressions
      
      // Helper to check if expression is a HAS operation on a specific field
      const isHasOperation = (expr: any, fieldName: string) =>
        expr.fieldName === fieldName && expr.operator === 'HAS'
      
      // Check if it's the pattern: (name HAS "value" OR tags HAS "value") with same value
      const isNameOrTagsPattern =
        expr1.value === expr2.value &&
        ((isHasOperation(expr1, 'name') && isHasOperation(expr2, 'tags')) ||
         (isHasOperation(expr1, 'tags') && isHasOperation(expr2, 'name')))
      
      if (isNameOrTagsPattern) {
        // Extract the common value as q
        extractedQ = expr1.value as string
        
        // Remove the OR expression from the flattened JSON
        if (hasOrAtTopLevel) {
          // If OR is at top level, we need to restructure
          modifiedFlattenedJson = {
            boolean: 'and',
            expressions: []
          }
        } else if (hasOrAtFirstLevel) {
          // Remove the OR expression from expressions
          modifiedFlattenedJson = {
            ...flattenedJson,
            expressions: flattenedJson.expressions?.filter(expr => 
              !(typeof expr === 'object' && expr.boolean === 'or')
            ) || []
          }
        } else if (hasOrInExpressions && parentIndex >= 0 && orExpressionIndex >= 0) {
          // Remove the OR expression from within expressions
          const newExpressions = [...(flattenedJson.expressions || [])]
          const parentExpr = { ...newExpressions[parentIndex] }
          parentExpr.expressions = parentExpr.expressions.filter((_, index) => index !== orExpressionIndex)
          newExpressions[parentIndex] = parentExpr
          
          modifiedFlattenedJson = {
            ...flattenedJson,
            expressions: newExpressions
          }
        }
        
        // Generate modified search query string (remove the OR part)
        const orPattern = new RegExp(`\\(name HAS "${extractedQ}" OR tags HAS "${extractedQ}"\\)|name HAS "${extractedQ}" OR tags HAS "${extractedQ}"`, 'gi')
        modifiedSearchQuery = searchQuery.replace(orPattern, '').trim()
        
        // Clean up any remaining AND/OR operators
        modifiedSearchQuery = modifiedSearchQuery
          .replace(/^\s*AND\s+/i, '')
          .replace(/^\s*OR\s+/i, '')
          .replace(/\s+AND\s*$/i, '')
          .replace(/\s+OR\s*$/i, '')
          .replace(/\s+AND\s+AND\s+/gi, ' AND ')
          .replace(/\s+OR\s+OR\s+/gi, ' OR ')
      }
    }
  }

  // Step 5: Check nesting level early (on modified structure after OR extraction)
  const nestingLevel = getNestingLevel(modifiedFlattenedJson)
  if (nestingLevel > 1) {
    throw new Error(
      `Nesting level ${nestingLevel} is not supported. Only single level queries are supported.`,
    )
  }

  // Step 6: Check for remaining OR early (before wrapping)
  if (containsOr(modifiedFlattenedJson)) {
    throw new Error('Complex query detected - use query mode')
  }

  // Step 7: Consolidate all expressions (size + dimensions)
  // This function handles:
  //   - Dimension mode check → throws if unsupported
  //   - Size field consolidation
  //   - Dimension field consolidation
  const consolidatedJson = consolidateExpressions(modifiedFlattenedJson, customMetadataFields)

  // Step 7.5: Extract format expressions for fileType
  let extractedFileType: FileType | '' = ''
  let finalConsolidatedJson = consolidatedJson
  
  // Helper function to recursively find format expressions
  const findFormatExpressions = (expr: any): any[] => {
    if (!expr || typeof expr !== 'object') {
      return []
    }
    
    const formatExprs: any[] = []
    
    // Check if this expression is a format expression
    if (expr.fieldName === 'format' && 
        (expr.operator === 'IS_ONE_OF' || expr.operator === 'IS_NONE_OF') &&
        Array.isArray(expr.value)) {
      formatExprs.push(expr)
    }
    
    // Recursively check nested expressions
    if (expr.expressions && Array.isArray(expr.expressions)) {
      expr.expressions.forEach((subExpr: any) => {
        formatExprs.push(...findFormatExpressions(subExpr))
      })
    }
    
    return formatExprs
  }
  
  // Find all format expressions in the consolidated JSON
  const formatExpressions = findFormatExpressions(consolidatedJson)
  
  if (formatExpressions.length > 0) {
    // Take the first format expression (should only be one)
    const formatExpr = formatExpressions[0]
    const formats = formatExpr.value as string[]
    const operator = formatExpr.operator
    
    // Reverse map to fileType
    extractedFileType = reverseMapFormatsToFileType(formats, operator)
    
    // Remove format expressions from the consolidated JSON
    const removeFormatExpressions = (expr: any): any => {
      if (!expr || typeof expr !== 'object') {
        return expr
      }
      
      // If this is a format expression, return null to remove it
      if (expr.fieldName === 'format' && 
          (expr.operator === 'IS_ONE_OF' || expr.operator === 'IS_NONE_OF')) {
        return null
      }
      
      // Recursively process nested expressions
      if (expr.expressions && Array.isArray(expr.expressions)) {
        const filteredExpressions = expr.expressions
          .map((subExpr: any) => removeFormatExpressions(subExpr))
          .filter((subExpr: any) => subExpr !== null && subExpr !== undefined)
        
        // If all expressions were removed, handle the boolean node
        if (filteredExpressions.length === 0) {
          return null
        }
        
        // If only one expression remains, return it unwrapped
        if (filteredExpressions.length === 1 && expr.boolean) {
          return filteredExpressions[0]
        }
        
        return {
          ...expr,
          expressions: filteredExpressions
        }
      }
      
      return expr
    }
    
    finalConsolidatedJson = removeFormatExpressions(consolidatedJson)
    
    // Clean up: if finalConsolidatedJson is null, create empty structure
    if (!finalConsolidatedJson) {
      finalConsolidatedJson = {
        boolean: 'and',
        expressions: []
      }
    }
    
    // Remove format clause from search query string
    // Pattern: (format (IN|NOT IN) [...]) or format (IN|NOT IN) [...]
    const formatPatterns = [
      // Pattern with parentheses: (format IN ["jpg", "png"])
      /\s*\(format\s+(?:IN|NOT\s+IN)\s+\[[^\]]+\]\)\s*/gi,
      // Pattern without parentheses: format IN ["jpg", "png"]
      /\s*format\s+(?:IN|NOT\s+IN)\s+\[[^\]]+\]\s*/gi,
    ]
    
    formatPatterns.forEach(pattern => {
      modifiedSearchQuery = modifiedSearchQuery.replace(pattern, ' ').trim()
    })
    
    // Clean up any remaining AND/OR operators
    modifiedSearchQuery = modifiedSearchQuery
      .replace(/^\s*AND\s+/i, '')
      .replace(/^\s*OR\s+/i, '')
      .replace(/\s+AND\s*$/i, '')
      .replace(/\s+OR\s*$/i, '')
      .replace(/\s+AND\s+AND\s+/gi, ' AND ')
      .replace(/\s+OR\s+OR\s+/gi, ' OR ')
      .replace(/\s*\(\s*\)/g, '') // Remove empty parentheses
  }

  // Step 8: Check for duplicate fields

  if (containsDuplicateField(finalConsolidatedJson)) {
    throw new Error('Complex query detected - use query mode')
  }

  // Step 9: Wrap in filterGroups structure (final format conversion)
  const result = wrapInFilterGroups(finalConsolidatedJson)
  if (!result) {
    throw new Error('Failed to wrap search query in filterGroups')
  }

  // Step 10: Return the result with extracted q, fileType, and modified search query
  return {
    searchQueryJson: result,
    q: extractedQ,
    fileType: extractedFileType,
    searchQuery: modifiedSearchQuery
  }
}

/**
 * Generates a standardized search query string from various parameters.
 * This ensures consistent formatting across all search query generation points.
 * 
 * @param params - Search query parameters
 * @returns Normalized search query string
 */
export const buildSearchQuery = ({
  // Type/Asset filtering
  assetType, // 'file' | 'folder' | 'media-collection' | null
  isVersionIncludedInSearch = false,
  
  // Text search
  q = '', // Search term
  searchMode = null, // 'keyword' | 'ai-text' | 'ai-image' | null
  searchInputMode = 'builder', // 'builder' | 'query'
  searchByTags = false,
  
  // Existing query
  searchQuery = '', // Existing search query string
  
  // File type filter
  fileType = '', // FileType | ""
}: {
  assetType?: 'file' | 'folder' | 'media-collection' | null
  isVersionIncludedInSearch?: boolean
  q?: string
  searchMode?: string | null
  searchInputMode?: 'builder' | 'query'
  searchByTags?: boolean
  searchQuery?: string
  fileType?: string
}): string => {
  const parts: string[] = []
  
  // 1. Type field (for file/folder filtering)
  if (assetType === 'file') {
    if (isVersionIncludedInSearch) {
      parts.push('(type IN ["file", "file-version"])')
    } else {
      parts.push('(type="file")')
    }
  } else if (assetType === 'folder') {
    parts.push('(type="folder")')
  }
  // Note: media-collection doesn't need type field
  
  // 2. Add existing searchQuery if provided (before q to match old ordering)
  if (searchQuery && searchQuery.trim().length > 0) {
    const trimmedSearchQuery = searchQuery.trim()
    parts.push(trimmedSearchQuery)
  }
  
  // 3. Handle q (text search) based on searchMode and searchInputMode
  if (q && q.trim().length > 0) {
    const trimmedQ = q.trim()
    
    // AI modes: q is handled separately (semantic/semanticUrl params), not in searchQuery
    if (searchMode === 'ai-text' || searchMode === 'ai-image') {
      // Don't add q to searchQuery for AI modes
    } else {
      // build name/tags/id query with single parentheses
      const conditions: string[] = []
      conditions.push(`name HAS "${trimmedQ}"`)
      
      if (searchByTags) {
        conditions.push(`tags HAS "${trimmedQ}"`)
      }
      
      if (CheckValidObjectId(trimmedQ)) {
        conditions.push(`id = "${trimmedQ}"`)
      }
      
      // Single parentheses wrapping all OR conditions
      const nameAndTagsQuery = conditions.length > 1 
        ? `(${conditions.join(' OR ')})`
        : conditions[0]
      
      parts.push(nameAndTagsQuery)
    }
  }
  
  // 4. Add fileType filter
  if (fileType && fileType.trim().length > 0 && fileTypeMapping[fileType]) {
    const fileTypeQueryOperator = fileType === 'others' ? 'NOT IN' : 'IN'
    const formats = fileTypeMapping[fileType]
    
    if (formats && formats.length > 0) {
      const fileTypeQuery = `(format ${fileTypeQueryOperator} [${formats
        .map(x => `"${x}"`)
        .join(',')}])`
      parts.push(fileTypeQuery)
    }
  }
  
  // 5. Combine all parts with consistent AND operator and spacing
  // Remove empty parts and join with " AND " (space before and after)
  const nonEmptyParts = parts.filter(part => part && part.trim().length > 0)
  
  if (nonEmptyParts.length === 0) {
    return ''
  }
  
  // Join with consistent spacing: " AND " (space before and after)
  return nonEmptyParts.join(' AND ')
}
